import moment from 'moment';
import XLSX from 'xlsx';
import { getAccountByKey } from './cheatSheet';

const DATE_PARSE_FORMATS = ['YYYY-MM-DD', 'DD/MM/YYYY', 'D/M/YYYY', 'DD-MM-YYYY'];

export const JOURNAL_ENTRY_FECHA_COLUMN = 'Fecha (DD/MM/YYYY)';

export const defaultJournalEntryImportDate = () =>
  moment().startOf('month').format('YYYY-MM-DD');

export const defaultJournalEntryImportDateDisplay = () =>
  moment().startOf('month').format('DD/MM/YYYY');

export function parseExcelDateCell(cell) {
  if (!cell) return '';
  if (cell.t === 'n' && typeof cell.v === 'number') {
    const parsed = XLSX.SSF.parse_date_code(cell.v);
    if (parsed) {
      const m = moment({ year: parsed.y, month: parsed.m - 1, day: parsed.d });
      if (m.isValid()) return m.format('YYYY-MM-DD');
    }
  }
  const text = String(cell.w ?? cell.v ?? '').trim();
  if (!text) return '';
  const strict = moment(text, DATE_PARSE_FORMATS, true);
  if (strict.isValid()) return strict.format('YYYY-MM-DD');
  const loose = moment(text);
  return loose.isValid() ? loose.format('YYYY-MM-DD') : '';
}

function buildAccount(accountCode, accountNameFromExcel, accounts = []) {
  const accountFromPlan = getAccountByKey(accountCode, accounts);
  if (accountFromPlan) {
    return {
      key: accountFromPlan.key,
      name: accountNameFromExcel || accountFromPlan.name || '',
      label: accountFromPlan.label || accountFromPlan.name || '',
    };
  }
  // El plan de cuentas está cargado y el código no existe en él: tratar la
  // cuenta como faltante (account null) para que caiga en la misma validación
  // "Faltan cuentas" que una línea manual sin cuenta seleccionada.
  if (accounts?.length) {
    return null;
  }
  // El plan aún no está disponible (contexto sin cargar): mantener el código
  // crudo para no marcar falsos faltantes.
  return {
    key: accountCode,
    name: accountNameFromExcel || accountCode,
    label: accountNameFromExcel || accountCode,
  };
}

function finalizeDraft(draft) {
  return {
    stringDate: draft.stringDate,
    description: draft.description || '',
    entries: draft.entries.map((entry, index) => ({
      ...entry,
      ranking: index + 1,
    })),
  };
}

export function getDraftValidation(draft) {
  if (!draft?.entries?.length) {
    return {
      isBalanced: false,
      hasLines: false,
      hasEmptyAccounts: false,
      debitTotal: 0,
      creditTotal: 0,
      isValid: false,
    };
  }

  const debitTotal = draft.entries.reduce(
    (sum, e) => sum + (parseFloat(e.debit) || 0),
    0
  );
  const creditTotal = draft.entries.reduce(
    (sum, e) => sum + (parseFloat(e.credit) || 0),
    0
  );
  const isBalanced = Math.abs(debitTotal - creditTotal) < 0.01;
  const hasAmount = (value) => {
    const num = parseFloat(value);
    return !Number.isNaN(num) && num > 0;
  };
  const hasEmptyAccounts = draft.entries.some(
    (e) => (hasAmount(e.debit) || hasAmount(e.credit)) && !e.account
  );
  const hasLines = draft.entries.some(
    (e) => e.account && (hasAmount(e.debit) || hasAmount(e.credit))
  );

  return {
    isBalanced,
    hasLines,
    hasEmptyAccounts,
    debitTotal,
    creditTotal,
    isValid: hasLines && isBalanced && !hasEmptyAccounts,
  };
}

/**
 * @param {object} sheet - hoja XLSX ya leída
 * @param {Array} accounts - plan de cuentas (desde AccountsContext) para validar códigos
 * @returns {{ drafts: Array<{ stringDate: string, description: string, entries: object[] }>, error?: string, missingAccountKeys?: string[] }}
 */
export function parseJournalEntriesSheet(sheet, accounts = []) {
  if (!sheet?.['!ref']) {
    return { drafts: [], error: 'El archivo está vacío.' };
  }

  const range = XLSX.utils.decode_range(sheet['!ref']);
  const getCell = (r, c) => sheet[XLSX.utils.encode_cell({ r, c })] || null;

  const getCellText = (r, c) => {
    const cell = getCell(r, c);
    if (!cell) return '';
    return String(cell.w ?? cell.v ?? '').trim();
  };

  const getCellAmount = (r, c) => {
    const cell = getCell(r, c);
    if (!cell) return 0;
    if (cell.t === 'n' && typeof cell.v === 'number') return cell.v;
    const raw = String(cell.w ?? cell.v ?? '').trim();
    if (!raw || raw === '-' || raw === ' - ') return 0;
    return parseFloat(raw.replace(/\./g, '').replace(',', '.')) || 0;
  };

  const headerRow = range.s.r;
  const colIndex = {};
  for (let c = range.s.c; c <= range.e.c; c++) {
    const cell = getCell(headerRow, c);
    if (cell) {
      const key = String(cell.v ?? cell.w ?? '').trim();
      if (key) colIndex[key] = c;
    }
  }

  const findCol = (...names) => {
    for (const n of names) if (colIndex[n] !== undefined) return colIndex[n];
    return -1;
  };

  const colFecha = findCol(
    JOURNAL_ENTRY_FECHA_COLUMN,
    'Fecha (dd/mm/yyyy)',
    'FECHA (DD/MM/YYYY)',
    'Fecha',
    'FECHA'
  );
  const colCodigo = findCol('Código', 'Codigo', 'CÓDIGO', 'CODIGO');
  const colNombre = findCol('Nombre (opcional)', 'Nombre (Opcional)', 'NOMBRE (OPCIONAL)');
  const colDebe = findCol('Debe', 'DEBE');
  const colHaber = findCol('Haber', 'HABER');
  const colRut = findCol(
    'RUT (opcional)',
    'RUT (Opcional)',
    'RUT (OPCIONAL)',
    'Rut (opcional)'
  );
  const colRazon = findCol(
    'Razon social (opcional)',
    'Razon Social (opcional)',
    'RAZON SOCIAL (OPCIONAL)',
    'Razón social (opcional)'
  );
  const colGlosa = findCol(
    'Glosa (opcional)',
    'Glosa (Opcional)',
    'GLOSA (OPCIONAL)'
  );

  if (colCodigo === -1) {
    return {
      drafts: [],
      error:
        'El archivo no tiene la columna "Código". Por favor, verifica que uses la plantilla correcta.',
    };
  }

  const hasFechaColumn = colFecha >= 0;
  const defaultDate = defaultJournalEntryImportDate();
  const drafts = [];
  const missingAccountKeys = new Set();
  let currentDraft = null;
  let activeDate = defaultDate;

  const addLineFromRow = (draft, r) => {
    const accountCode = getCellText(r, colCodigo);
    if (!accountCode) return;

    const accountNameFromExcel = colNombre >= 0 ? getCellText(r, colNombre) : '';
    const debit = colDebe >= 0 ? getCellAmount(r, colDebe) : 0;
    const credit = colHaber >= 0 ? getCellAmount(r, colHaber) : 0;
    const rut = colRut >= 0 ? getCellText(r, colRut) : '';
    const businessName = colRazon >= 0 ? getCellText(r, colRazon) : '';

    const account = buildAccount(accountCode, accountNameFromExcel, accounts);
    if (!account) {
      missingAccountKeys.add(accountCode);
    }

    draft.entries.push({
      ranking: draft.entries.length + 1,
      account,
      debit,
      credit,
      rut: rut || null,
      businessName: businessName || null,
    });
  };

  const startBlock = (date, glosa) => {
    if (currentDraft?.entries?.length) {
      drafts.push(finalizeDraft(currentDraft));
    }
    currentDraft = {
      stringDate: date,
      description: glosa || '',
      entries: [],
    };
    activeDate = date;
  };

  for (let r = headerRow + 1; r <= range.e.r; r++) {
    const fecha =
      hasFechaColumn && colFecha >= 0
        ? parseExcelDateCell(getCell(r, colFecha))
        : '';
    const accountCode = getCellText(r, colCodigo);
    const glosaFirstRow = colGlosa >= 0 ? getCellText(r, colGlosa) : '';

    if (hasFechaColumn && fecha) {
      startBlock(fecha, glosaFirstRow);
      if (accountCode) addLineFromRow(currentDraft, r);
      continue;
    }

    if (!accountCode) continue;

    if (!currentDraft) {
      if (hasFechaColumn) {
        startBlock(activeDate, '');
      } else {
        currentDraft = {
          stringDate: activeDate,
          description: '',
          entries: [],
        };
      }
    }

    addLineFromRow(currentDraft, r);
  }

  if (currentDraft?.entries?.length) {
    drafts.push(finalizeDraft(currentDraft));
  }

  if (drafts.length === 0) {
    return {
      drafts: [],
      error: 'El archivo no contiene filas válidas con códigos de cuenta.',
    };
  }

  return { drafts, missingAccountKeys: Array.from(missingAccountKeys) };
}

export function draftToFormData(draft, baseFormData = {}) {
  const stringDate = draft.stringDate || defaultJournalEntryImportDate();
  const parsedDate = moment(stringDate, 'YYYY-MM-DD', true);
  const safeDate = parsedDate.isValid() ? parsedDate : moment(stringDate);
  return {
    ...baseFormData,
    description: draft.description || '',
    stringDate: safeDate.format('YYYY-MM-DD'),
    entryDate: {
      day: safeDate.format('DD'),
      month: safeDate.format('MM'),
      year: safeDate.format('YYYY'),
      fullDate: new Date(safeDate.year(), safeDate.month(), safeDate.date(), 15, 0, 0),
    },
    entries: (draft.entries || []).map((entry, index) => ({
      ...entry,
      ranking: index + 1,
      debit:
        entry.debit === '' || entry.debit == null
          ? ''
          : String(entry.debit),
      credit:
        entry.credit === '' || entry.credit == null
          ? ''
          : String(entry.credit),
    })),
  };
}

export function formDataToDraft(formData) {
  return {
    stringDate: formData.stringDate,
    description: formData.description || '',
    entries: (formData.entries || []).map((entry, index) => ({
      ...entry,
      ranking: index + 1,
      debit: entry.debit === '' ? '' : parseFloat(entry.debit) || 0,
      credit: entry.credit === '' ? '' : parseFloat(entry.credit) || 0,
    })),
  };
}
